import React, { createContext, useContext, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from 'react-apollo'
import { useRestaurant } from '../../RestaurantProvider/RestaurantProvider'
import { useFulfillment } from '../../FulfillmentProvider/FulfillmentProvider'
import { REORDER_AND_REPLACE_CART } from '../../../apollo/cart/cart.graphql'
import { dataByTypename } from '../../../utils/apollo-helpers'
import { useCart } from '../../CartProvider/CartProvider'
import { PUSH_MODIFICATION_ERRORS, CLEAR_MODIFICATION_ERRORS } from '../../CartProvider/cart-actions'

const ReorderContext = createContext({})

export const MODIFICATION_ERROR_SOURCE_REORDER = 'REORDER'
const mapWithSource = error => ({ ...error, source: MODIFICATION_ERROR_SOURCE_REORDER })

export const ReorderProvider = ({ children }) => {
  const { restaurantGuid } = useRestaurant()
  const { updateCartGuid, dispatch } = useCart()
  const { fulfillmentType, fulfillmentTime, diningOptionBehavior, deliveryInfo } = useFulfillment()
  const [reorderGuid, setReorderGuid] = useState(null)

  const [mutate, reorderMutationResult] = useMutation(REORDER_AND_REPLACE_CART, {
    onCompleted: (data) => {
      dispatch({ type: CLEAR_MODIFICATION_ERRORS })

      const { ReorderResponse, ReorderError } = dataByTypename(data.reorderV2)

      const cartModificationErrors = []
      if (ReorderResponse) {
        const { cart: { guid: cartGuid }, warningsV2 } = ReorderResponse
        updateCartGuid(cartGuid)
        cartModificationErrors.push(...warningsV2.map(mapWithSource))
      }

      if (ReorderError) {
        cartModificationErrors.push(mapWithSource(ReorderError))
      }

      if (cartModificationErrors.length) {
        dispatch({
          type: PUSH_MODIFICATION_ERRORS,
          modificationErrors: cartModificationErrors
        })
      }
    }
  })

  const reorder = useCallback((orderGuid) => {
    setReorderGuid(orderGuid)

    return mutate({
      variables: {
        input: {
          orderGuid,
          restaurantGuid,
          orderSource: 'ONLINE',
          cartFulfillmentInput: {
            fulfillmentType,
            fulfillmentDateTime: fulfillmentTime,
            diningOptionBehavior,
            deliveryInfo
          }
        }
      }
    })
  }, [deliveryInfo, diningOptionBehavior, fulfillmentTime, fulfillmentType, mutate, restaurantGuid])

  const context = {
    reorderGuid,
    reorderMutation: [reorder, reorderMutationResult]
  }

  return (
    <ReorderContext.Provider value={context}>
      {children}
    </ReorderContext.Provider>
  )
}

ReorderProvider.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * Hook that returns the Reorder context
 * @returns {{ reorderGuid: number, reorderMutation: Function }}
 */
export const useReorder = () => useContext(ReorderContext)
